import React, { useState } from 'react';

import classNames from 'classnames';
import { Formik, FormikProps } from 'formik';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

import Dialog from '@material-ui/core/Dialog';
import InputAdornment from '@material-ui/core/InputAdornment';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';

import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import VisibilityOutlined from '@material-ui/icons/VisibilityOutlined';
import VisibilityOffOutlined from '@material-ui/icons/VisibilityOffOutlined';

import Button from 'components/CustomButtons/Button';
import { BodyMBold } from 'components/Typography/Typography';
import validationProps from 'components/PaymentMethod/helper/validation';

import { BankPaymentDetails, BOMPaymentToken, PayWith } from '../../type';
import { getPaymentMethodLabels } from 'redux/modules/dynamicLabels/selectors';

import { FormControlLabel, makeStyles } from '@material-ui/core';
import styles, { AntSwitch as Switch } from 'assets/jss/material-dashboard-pro-react/components/paymentMethodsStyle';

interface AddCardProps {
	onClose: () => void;
	isOpen: boolean;
	onAddCard: (_token: BOMPaymentToken) => void;
}

// @ts-ignore
const useStyles = makeStyles(styles);

const AddBankDetails = ({ isOpen, onAddCard, onClose }: AddCardProps) => {
	const classes = useStyles();
	const paymentMethodLabels = useSelector(getPaymentMethodLabels);

	const [showPassword, setShowPassword] = useState<boolean>(false);

	const handleClickShowPassword = () => {
		setShowPassword((passwordShow) => !passwordShow);
	};

	const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => event?.preventDefault();

	const onSubmit = (values: BankPaymentDetails) => {
		onAddCard({
			default: false,
			id: (+new Date()).toString(),
			mobileNumber: values.mobile,
			paymentAliasId: values.paymentAlias,
			tokenType: PayWith.BM,
			isUnSaved: true,
			savedOption: values.savedOption,
		});
	};

	const allowedDomains = paymentMethodLabels?.paymentAlias ?? ['OMTL'];
	const domainRegex = new RegExp(`^[0-9A-Za-z]{8}@(${allowedDomains.join('|')})$`);

	const FormSchema = Yup.object().shape({
		mobile: Yup.string()
			.required(paymentMethodLabels?.mobileRequired)
			.matches(/^\d{8}$/, paymentMethodLabels?.mobileInvalid)
			.test('not-blank', paymentMethodLabels?.mobileRequired, (value) => {
				return value?.trim() !== '';
			}),
		paymentAlias: Yup.string()
			.required(paymentMethodLabels?.paymentsAliasIDRequired)
			.matches(domainRegex, paymentMethodLabels?.paymentsAliasIDInvalid)
			.test('not-blank', paymentMethodLabels?.paymentsAliasIDRequired, (value) => {
				return value?.trim() !== '';
			}),
	});

	return (
		<Dialog aria-labelledby="customized-dialog-title" maxWidth="xs" fullWidth open={isOpen}>
			<MuiDialogTitle disableTypography>
				<BodyMBold customClassName={classes.marginBottomNone}>
					{paymentMethodLabels?.add_payment_alias}
				</BodyMBold>
				{onClose && (
					<IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
						<CloseIcon />
					</IconButton>
				)}
			</MuiDialogTitle>
			<MuiDialogContent dividers className={classes.addCardDialog}>
				<Formik
					onSubmit={onSubmit}
					validationSchema={FormSchema}
					initialValues={{ mobile: '', paymentAlias: '', savedOption: false }}
				>
					{(props: FormikProps<BankPaymentDetails>) => {
						return (
							<>
								<div className={classes.billDetailsContainer}>
									<TextField
										className={classes.billingEmailInput}
										style={{ margin: 8 }}
										fullWidth
										placeholder={paymentMethodLabels?.mobileNumber}
										margin="normal"
										InputLabelProps={{
											shrink: true,
										}}
										autoComplete="off"
										onChange={props.handleChange}
										onBlur={props.handleBlur}
										inputProps={{ maxLength: 40 }}
										value={props.values.mobile}
										{...validationProps('mobile', props.errors, props.touched)}
									/>
								</div>
								<div className={classes.billDetailsContainer}>
									<TextField
										fullWidth
										placeholder={paymentMethodLabels?.paymentsAliasID}
										type={showPassword ? 'text' : 'password'}
										autoComplete="off"
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														size="small"
														onClick={handleClickShowPassword}
														onMouseDown={handleMouseDownPassword}
														aria-label={paymentMethodLabels?.togglePasswordVisibility}
													>
														{showPassword ? (
															<VisibilityOffOutlined fontSize="small" />
														) : (
															<VisibilityOutlined fontSize="small" />
														)}
													</IconButton>
												</InputAdornment>
											),
										}}
										inputProps={{ maxLength: 40 }}
										value={props.values.paymentAlias}
										onChange={props.handleChange}
										onBlur={props.handleBlur}
										{...validationProps('paymentAlias', props.errors, props.touched)}
									/>
								</div>
								<div className={classNames(classes.billDetailsContainer, classes.paddingBottomNone)}>
									<p className={classNames(classes.infoNote, classes.paymentNote)}>
										{paymentMethodLabels?.bom_payment_approve_note}
									</p>
								</div>
								<div className={classNames(classes.billDetailsContainer, classes.paddingBottomNone)}>
									<div className={classNames(classes.accordionElement, classes.bottomSpace)}>
										<FormControlLabel
											className={classes.switchLabel}
											control={
												<Switch
													id="save_card_details"
													name="savedOption"
													onChange={props.handleChange}
												/>
											}
											label={paymentMethodLabels?.saveMyCard}
										/>
									</div>
								</div>
								<div className={classNames(classes.billDetailsContainer, classes.addCardDialogFooter)}>
									<Button color="secondary-light" onClick={onClose}>
										{paymentMethodLabels.cancel}
									</Button>
									<Button type="submit" color="primary-light" onClick={props.submitForm}>
										{paymentMethodLabels.add}
									</Button>
								</div>
							</>
						);
					}}
				</Formik>
			</MuiDialogContent>
		</Dialog>
	);
};

export default AddBankDetails;
